import type { EmptyBoardMessage } from '../reportScopes';

/**
 * Empty-board tab — port of tabs/empty_board/tmpl/empty_board.tmpl (+ view).
 * Which message div carries `.active` is decided by the page via
 * computeEmptyBoardMessage (controller/action.js:26-59). The submittal hint
 * gets `.active` when the current report is submittal_package
 * (menu.js:109-112 / main_header.js:379).
 *
 * The whole #generate_report_icon block is clickable; the legacy view only
 * acts when .can-generate is active (empty_board.js:36-41). The page's
 * onGenerate arms the tabs preloader + 'is-generate' flag (:38-39) and runs
 * the 5b generation kit.
 */
interface EmptyBoardProps {
  message: EmptyBoardMessage;
  submittalHint: boolean;
  onGenerate: () => void;
}

const EmptyBoard = ({ message, submittalHint, onGenerate }: EmptyBoardProps) => {
  const active = (key: EmptyBoardMessage) => (message === key ? ' active' : '');

  const onClick = () => {
    // empty_board.js:37 — only the can-generate state triggers generation.
    if (message !== 'can-generate') return;
    onGenerate();
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="no-report-selected" id="generate_report_icon" onClick={onClick}>
      <span className="no-report-icon">
        <i className="icon-report" />
      </span>
      <div className={`select-report${active('select-report')}`}>
        <h3>To Select a Report</h3>
        <ul>
          <li>
            <span>1.</span>
            <span>Select a Project or &apos;All&apos;</span>
          </li>
          <li>
            <span>2.</span>
            <span>Select an Air Handling Unit or &apos;All&apos;</span>
          </li>
          <li>
            <span>3.</span>
            <span>Select a Report</span>
          </li>
          <li>
            <span>4.</span>
            <span>
              Generate the Report by clicking on the <i className="icon-refresh-1" /> icon
            </span>
          </li>
        </ul>
      </div>
      <div className={`select-project-msg${active('select-project-msg')}`}>
        <h3>Select Project</h3>
      </div>
      <div className={`select-project-ahu-msg${active('select-project-ahu-msg')}`}>
        <h3>Select Project and AHU</h3>
      </div>
      <div className={`can-generate${active('can-generate')}`}>
        <ul>
          <li>
            <span>
              Click{' '}
              <button
                type="button"
                id="generate_report_icon_btn"
                className="updade-btn icon-refresh-1 ka-has-tooltip"
                tooltip="Generate"
              />{' '}
              to Generate Report
            </span>
          </li>
        </ul>
        <div id="hint-for-submittal-package" className={submittalHint ? 'active' : ''}>
          <ol type="1">
            <li>
              <span>1.</span> Verify that all Air Handling Units in the project are free of
              errors, prior to generating the Submittal Package report.
              <p>
                The Submittal Package report will not generate if any of the AHU&apos;s has one
                or more errors in it.
              </p>
            </li>
            <li>
              <span>2.</span> Ensure that no changes to the project or any of the AHU&apos;s in
              the project are made, during the generation of the Submittal Package report.
            </li>
            <li>
              <span>3.</span> You may navigate away from the Report Board, while the Submittal
              Package report is being generated.
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default EmptyBoard;
